import React, { useCallback, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
  margin-bottom: ${rem(-8)};
`

const messages = defineMessages({
  morning: {
    id: 'QG2XM-mi9lejKz3_md6R',
    defaultMessage: 'Morning (6am–12pm)',
    description: 'Time slot filter option for experiences starting in the morning',
  },
  afternoon: {
    id: 'pPyZGRpHaRBFzGE_UzfA',
    defaultMessage: 'Afternoon (12pm–6pm)',
    description: 'Time slot filter option for experiences starting in the afternoon',
  },
  evening: {
    id: 'yW1nHz16fgyJSenhi45Q',
    defaultMessage: 'Evening (6pm+)',
    description: 'Time slot filter option for experiences starting in the evening',
  },
})

const TIME_SLOT_OPTIONS: Array<{
  value: Experiences.TimeSlotFilter;
  messageKey: keyof typeof messages;
}> = [
  { value: 'morning', messageKey: 'morning' },
  { value: 'afternoon', messageKey: 'afternoon' },
  { value: 'evening', messageKey: 'evening' },
]

interface Props {
  timeSlotCounts?: Partial<Record<Experiences.TimeSlotFilter, number>>;
  compact?: boolean;
  hideCounts?: boolean;
}

function ExperienceSearchStartTimeFilter({ timeSlotCounts, compact = false, hideCounts = false }: Props) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()

  const selectedSlots = useMemo(
    () => searchFilters.timeSlots ?? [],
    [searchFilters.timeSlots],
  )

  const onToggle = useCallback(
    (value: Experiences.TimeSlotFilter) => {
      const option = TIME_SLOT_OPTIONS.find(option => option.value === value)
      const selected = selectedSlots.includes(value)
      if (option) {
        trackSearchFilter({
          label: intl.formatMessage(messages[option.messageKey]),
          type: 'starting_time',
          value: selected ? 'unselected' : 'selected',
        })
      }
      const next = selectedSlots.includes(value)
        ? selectedSlots.filter((v) => v !== value)
        : [...selectedSlots, value]
      setSearchFilters({
        ...searchFilters,
        timeSlots: next.length > 0 ? next : undefined,
      })
    },
    [intl, selectedSlots, searchFilters, setSearchFilters, trackSearchFilter],
  )

  const compactItems = useMemo<Array<FilterPanelCheckItem>>(() => {
    return TIME_SLOT_OPTIONS.map((option) => ({
      value: option.value,
      label: intl.formatMessage(messages[option.messageKey]),
      checked: selectedSlots.includes(option.value),
      count: timeSlotCounts?.[option.value],
      hideCount: hideCounts,
      disabled: timeSlotCounts?.[option.value] === 0,
    }))
  }, [hideCounts, intl, selectedSlots, timeSlotCounts])

  const onCompactToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(event.target.value as Experiences.TimeSlotFilter)
  }, [onToggle])

  if (compact) {
    return (
      <FilterPanelCheckboxGroup
        name="timeSlots"
        items={compactItems}
        onChange={onCompactToggle}
      />
    )
  }

  return (
    <ListContainer>
      {TIME_SLOT_OPTIONS.map((option) => (
        <CheckboxInputListItem
          key={option.value}
          inputPlacement="start"
          checked={selectedSlots.includes(option.value)}
          onChange={() => onToggle(option.value)}
          title={intl.formatMessage(messages[option.messageKey])}
          size="medium"
          disabled={timeSlotCounts?.[option.value] === 0}
        />
      ))}
    </ListContainer>
  )
}

export default ExperienceSearchStartTimeFilter
